// src/components/connector/ConnectorStatus.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Grid, 
  Alert,
  Skeleton,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getChargerConnectors, getChargerStatus } from '../../api/chargerAPI';
import ConnectorStatusCard from './ConnectorStatusCard';
import LoadingSpinner from '../common/Loadingspinner';

const ConnectorStatus = ({ 
  chargerId, 
  companyId, 
  siteId, 
  highlightConnector = null,
  showTitle = true,
  compact = false,
  maxConnectors = null,
  useStatusEndpoint = false // Use status endpoint instead of connectors endpoint
}) => {
  // Fetch connector data
  const { 
    data: connectors, 
    isLoading, 
    isError, 
    error,
    refetch,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['charger-connectors', chargerId, companyId, siteId, useStatusEndpoint],
    queryFn: async () => {
      console.log('🔌 Fetching connectors:', { chargerId, companyId, siteId, useStatusEndpoint });
      
      try {
        let result;
        if (useStatusEndpoint) {
          console.log('📊 Using status endpoint');
          const data = await getChargerStatus(chargerId, companyId, siteId);
          console.log('📊 Status endpoint response:', data);
          result = data.connector_statuses || [];
        } else {
          console.log('🔌 Using connectors endpoint');
          result = await getChargerConnectors(chargerId, companyId, siteId);
          console.log('🔌 Connectors endpoint response:', result);
        }
        
        console.log('✅ Final connectors data:', result);
        return result;
      } catch (err) {
        console.error('❌ Error fetching connectors:', err);
        console.error('❌ Error details:', err.response?.data || err.message);
        throw err;
      }
    },
    enabled: !!(chargerId && companyId && siteId),
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    refetchIntervalInBackground: false,
    staleTime: 10000 // Consider data stale after 10 seconds
  });

  // Loading state
  if (isLoading) {
    return (
      <Box>
        {showTitle && (
          <Box display="flex" alignItems="center" mb={3}>
            <EvStationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6">Connector Status</Typography>
          </Box>
        )}
        <Grid container spacing={compact ? 1 : 2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={compact ? 6 : 4} key={i}>
              <Skeleton variant="rectangular" height={compact ? 120 : 180} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error state
  if (isError) {
    console.error('🚨 ConnectorStatus Error State:', error);
    console.error('🚨 Error response:', error?.response);
    console.error('🚨 Error data:', error?.response?.data);
    
    return (
      <Box>
        {showTitle && (
          <Box display="flex" alignItems="center" mb={3}>
            <EvStationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6">Connector Status</Typography>
          </Box>
        )}
        <Alert 
          severity="error"
          action={
            <Chip 
              icon={<RefreshIcon />}
              label="Retry"
              onClick={() => refetch()}
              clickable
              size="small"
            />
          }
        >
          <Typography variant="body2" component="div">
            <strong>Failed to load connector status</strong>
          </Typography>
          <Typography variant="body2" component="div">
            Error: {error?.message || 'Unknown error'}
          </Typography>
          {error?.response?.status && (
            <Typography variant="body2" component="div">
              Status: {error.response.status}
            </Typography>
          )}
          {error?.response?.data && (
            <Typography variant="body2" component="div">
              Details: {JSON.stringify(error.response.data)}
            </Typography>
          )}
          <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
            Parameters: chargerId={chargerId}, companyId={companyId}, siteId={siteId}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // No connectors found
  if (!connectors || connectors.length === 0) {
    console.warn('⚠️ No connectors found:', { connectors, chargerId, companyId, siteId });
    
    return (
      <Box>
        {showTitle && (
          <Box display="flex" alignItems="center" mb={3}>
            <EvStationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6">Connector Status</Typography>
          </Box>
        )}
        <Alert severity="info">
          <Typography variant="body2" component="div">
            <strong>No connectors found for this charger</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
            Parameters: chargerId={chargerId}, companyId={companyId}, siteId={siteId}
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            Response: {JSON.stringify(connectors)}
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            Using status endpoint: {useStatusEndpoint ? 'Yes' : 'No'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Filter connectors if maxConnectors is specified
  const displayConnectors = maxConnectors 
    ? connectors.slice(0, maxConnectors)
    : connectors;

  // Sort connectors by ConnectorId
  const sortedConnectors = [...displayConnectors].sort((a, b) => 
    (a.ConnectorId || 0) - (b.ConnectorId || 0)
  );

  // Calculate last update time
  const lastUpdateTime = new Date(dataUpdatedAt).toLocaleTimeString();

  return (
    <Box>
      {/* Header */}
      {showTitle && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <EvStationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6">
              Connector Status ({sortedConnectors.length})
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Updated: {lastUpdateTime}
            </Typography>
            <Chip 
              icon={<RefreshIcon />}
              label="Refresh"
              onClick={() => refetch()}
              clickable
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      {/* Connector Cards Grid */}
      <Grid container spacing={compact ? 1 : 2}>
        {sortedConnectors.map((connector) => (
          <Grid 
            item 
            xs={6} 
            sm={compact ? 4 : 6} 
            md={compact ? 3 : 4} 
            lg={compact ? 3 : 3}
            key={`${connector.ConnectorId}-${connector.ConnectorChargerId}`}
          >
            <ConnectorStatusCard
              connector={connector}
              isHighlighted={highlightConnector === connector.ConnectorId}
              showDetails={!compact}
              compact={compact}
            />
          </Grid>
        ))}
      </Grid>

      {/* Show more connectors indicator */}
      {maxConnectors && connectors.length > maxConnectors && (
        <Box mt={2} textAlign="center">
          <Alert severity="info" sx={{ display: 'inline-flex' }}>
            Showing {maxConnectors} of {connectors.length} connectors
          </Alert>
        </Box>
      )}

      {/* Summary Statistics */}
      {!compact && sortedConnectors.length > 1 && (
        <Card sx={{ mt: 3, backgroundColor: 'grey.50' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Summary
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {['Available', 'Occupied', 'Faulted', 'Unavailable'].map(status => {
                const count = sortedConnectors.filter(c => 
                  c.ConnectorStatus?.toLowerCase() === status.toLowerCase()
                ).length;
                
                if (count === 0) return null;
                
                return (
                  <Chip
                    key={status}
                    label={`${status}: ${count}`}
                    size="small"
                    color={
                      status === 'Available' ? 'success' :
                      status === 'Occupied' ? 'primary' :
                      status === 'Faulted' ? 'error' : 'default'
                    }
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ConnectorStatus;